import { StyleSheet } from 'aphrodite';

import {
	borderColor,
	borderOutline,
	brandPrimaryHover,
	charmGray,
	darkGrayFontColor,
	white,
} from '../../styles/colors';

import { shadowMd } from '../../styles/shadows';
import { THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	dropdownPopup: {
		backgroundColor: THEME.colors.background.light,
		color: '#0F172A',
		transformOrigin: 'var(--transform-origin)',
		boxSizing: 'border-box',
		boxShadow: shadowMd,
		border: `1px solid ${borderColor}`,
		padding: 4,
		borderRadius: 6,
		transition: 'transform,scale,opacity',
		':is([data-ending-style])': {
			scale: 0.95,
			opacity: 0,
		},
		':is([data-starting-style])': {
			scale: 0.95,
			opacity: 0,
		},
		outline: 'none',
		zIndex: 5,
		position: 'relative',
		minWidth: 128,
		width: `var(--anchor-width)`,
	},
	dropdownPositioner: {
		zIndex: 5,
		maxHeight: `var(--available-height)`,
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	menuItem: {
		display: 'flex',
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: THEME.colors.text.dark,
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: `#f4f5f7`,
		},
		':has(*) svg': {
			pointerEvents: 'none',
			flexShrink: 0,
			width: 12,
			height: 12,
		},
	},
	separator: {
		margin: '4px -4px',
		height: 1,
		backgroundColor: borderOutline,
	},
	groupLabel: {
		padding: `6px 8px`,
		fontSize: '0.75rem',
		color: darkGrayFontColor,
	},
	checkboxItem: {
		display: `flex`,
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: THEME.colors.text.dark,
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: `#f4f5f7`,
		},
	},
	checkBoxItemCheckWrapper: {
		border: `1px solid ${charmGray}`,
		width: 12,
		height: 12,
		flexShrink: 0,
		borderRadius: 4,
		display: `flex`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkBoxItemCheckIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkBoxItemCheck: {
		width: 8,
		height: 8,
	},
	radioItem: {
		display: `flex`,
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: darkGrayFontColor,
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: `#f4f5f7`,
		},
	},
	radioItemCheckWrapper: {
		border: `1px solid ${charmGray}`,
		width: 12,
		height: 12,
		flexShrink: 0,
		borderRadius: '50%',
		display: `flex`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioItemCheckIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioItemCheck: {
		width: 8,
		height: 8,
	},
	subTrigger: {
		display: 'flex',
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 2,
		padding: `6px 8px`,
		fontSize: '0.875rem',
		outline: 'none',
		color: darkGrayFontColor,
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':is([data-highlighted])': {
			backgroundColor: brandPrimaryHover,
			color: white,
		},
		':has(*) svg': {
			pointerEvents: 'none',
			flexShrink: 0,
			width: 12,
			height: 12,
		},
	},
	subTriggerChevron: {
		marginLeft: 'auto',
		width: 12,
		height: 12,
	},
	selectTrigger: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: 36,
		width: 'fit-content',
		whiteSpace: 'nowrap',
		borderRadius: 6,
		border: `1px solid ${borderColor}`,
		backgroundColor: 'transparent',
		padding: `8px 12px`,
		fontSize: '0.875rem',
		lineHeight: '1.25rem',
		boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
		color: THEME.colors.text.dark,
		':focus': {
			outline: 'none',
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.5,
		},
		':has(*) > span': {
			overflow: 'hidden',
			display: '-webkit-box',
			'-webkit-box-orient': 'vertical',
			'-webkit-line-clamp': 1,
		},
	},
	ellipsisTrigger: {
		boxSizing: 'border-box',
		width: 16,
		height: 16,
		':has(*) > svg': {
			width: '100%',
			height: '100%',
		},
	},
	triggerChevron: {
		display: 'block',
		boxSizing: 'border-box',
		height: 16,
		width: 16,
		opacity: 0.5,
		flexShrink: 0,
	},
	// Combo Dropdown Trigger Styles
	comboTriggerWrapper: {
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		position: 'relative',
	},
	comboTriggerPrimary: {
		transition: 'color .5s, background-color .5s',
		alignItems: 'center',
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		borderRight: 'none',
		display: 'flex',
		fontSize: 14,
		fontFamily: THEME.typography.fontFamilies.lexend,
		justifyContent: 'center',
		textAlign: 'center',
		gap: 8,
		boxSizing: 'border-box',
		textDecoration: 'none',
		border: 'none',
		':has(*) > svg': {
			display: 'block',
			pointerEvents: 'none',
			flexShrink: 0,
			width: 16,
			height: 16,
		},
	},
	comboTriggerDropdown: {
		transition: 'color .5s, background-color .5s',
		alignItems: 'center',
		borderTopLeftRadius: 0,
		borderBottomLeftRadius: 0,
		borderLeft: `1px solid rgba(255, 255, 255, 0.2)`,
		display: 'flex',
		fontSize: 14,
		fontFamily: THEME.typography.fontFamilies.lexend,
		justifyContent: 'center',
		textAlign: 'center',
		gap: 8,
		boxSizing: 'border-box',
		textDecoration: 'none',
		border: 'none',
		background: 'transparent',
		width: 32,
		padding: 0,
	},
	comboTriggerChevron: {
		display: 'block',
		boxSizing: 'border-box',
		height: 16,
		width: 16,
		flexShrink: 0,
	},
	// Primary variant styles
	comboprimaryPrimary: {
		color: THEME.colors.background.light,
		background: THEME.colors.primary.main,
		borderTopLeftRadius: 8,
		borderBottomLeftRadius: 8,
		boxShadow: '0 1px 2px 0 rgba(140,140,140,0.50)',
		':active': {
			background: THEME.colors.primary.dark,
		},
		':hover': {
			background: THEME.colors.hover.main,
		},
	},
	comboprimaryDropdown: {
		color: THEME.colors.background.light,
		background: THEME.colors.primary.main,
		borderTopRightRadius: 8,
		borderBottomRightRadius: 8,
		boxShadow: '0 1px 2px 0 rgba(140,140,140,0.50)',
		':active': {
			background: THEME.colors.primary.dark,
		},
		':hover': {
			background: THEME.colors.hover.main,
		},
	},
	// Secondary variant styles
	combosecondaryPrimary: {
		color: THEME.colors.primary.main,
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.primary.main}`,
		borderTopLeftRadius: 8,
		borderBottomLeftRadius: 8,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		':active': {
			background: '#E6ECF3',
		},
		':hover': {
			background: '#EAF7FA',
			border: `1px solid ${THEME.colors.hover.main}`,
		},
	},
	combosecondaryDropdown: {
		color: THEME.colors.primary.main,
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.primary.main}`,
		borderTopRightRadius: 8,
		borderBottomRightRadius: 8,
		borderLeft: `1px solid ${THEME.colors.primary.main}`,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		':active': {
			background: '#E6ECF3',
		},
		':hover': {
			background: '#EAF7FA',
			border: `1px solid ${THEME.colors.hover.main}`,
		},
	},
	// Success variant styles
	combosuccessPrimary: {
		color: THEME.colors.background.light,
		background: THEME.colors.success.main,
		borderTopLeftRadius: 8,
		borderBottomLeftRadius: 8,
		boxShadow: '0 1px 2px 0 rgba(140,140,140,0.50)',
		':active': {
			background: `oklch(from ${THEME.colors.success.main} calc(l - 0.08) c h)`,
		},
		':hover': {
			background: `oklch(from ${THEME.colors.success.main} calc(l + 0.06) c h)`,
		},
	},
	combosuccessDropdown: {
		color: THEME.colors.background.light,
		background: THEME.colors.success.main,
		borderTopRightRadius: 8,
		borderBottomRightRadius: 8,
		boxShadow: '0 1px 2px 0 rgba(140,140,140,0.50)',
		':active': {
			background: `oklch(from ${THEME.colors.success.main} calc(l - 0.08) c h)`,
		},
		':hover': {
			background: `oklch(from ${THEME.colors.success.main} calc(l + 0.06) c h)`,
		},
	},
	// Link variant styles
	combolinkPrimary: {
		color: THEME.colors.primary.main,
		background: 'transparent',
		borderTopLeftRadius: 8,
		borderBottomLeftRadius: 8,
		boxShadow: 'none',
		':active': {
			background: 'transparent',
			color: THEME.colors.hover.main,
		},
		':hover': {
			background: 'transparent',
			color: THEME.colors.hover.main,
			textDecoration: 'underline',
		},
	},
	combolinkDropdown: {
		color: THEME.colors.primary.main,
		background: 'transparent',
		borderTopRightRadius: 8,
		borderBottomRightRadius: 8,
		borderLeft: `1px solid ${THEME.colors.primary.main}`,
		boxShadow: 'none',
		':active': {
			background: 'transparent',
			color: THEME.colors.hover.main,
		},
		':hover': {
			background: 'transparent',
			color: THEME.colors.hover.main,
		},
	},
	// Destructive variant styles
	combodestructivePrimary: {
		color: THEME.colors.danger.main,
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.danger.main}`,
		borderTopLeftRadius: 8,
		borderBottomLeftRadius: 8,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		':active': {
			background: THEME.colors.danger.light,
			border: `1px solid ${THEME.colors.danger.dark}`,
			color: THEME.colors.danger.main,
		},
		':hover': {
			background: THEME.colors.danger.light,
			border: `1px solid ${THEME.colors.danger.main}`,
			color: THEME.colors.danger.main,
		},
	},
	combodestructiveDropdown: {
		color: THEME.colors.danger.main,
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.danger.main}`,
		borderTopRightRadius: 8,
		borderBottomRightRadius: 8,
		borderLeft: `1px solid ${THEME.colors.danger.main}`,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		':active': {
			background: THEME.colors.danger.light,
			border: `1px solid ${THEME.colors.danger.dark}`,
			color: THEME.colors.danger.main,
		},
		':hover': {
			background: THEME.colors.danger.light,
			border: `1px solid ${THEME.colors.danger.main}`,
			color: THEME.colors.danger.main,
		},
	},
	// Size variations
	combodefault: {
		height: 36,
		padding: '10px 16px',
	},
	combosm: {
		height: 32,
		paddingLeft: 12,
		paddingRight: 12,
		gap: 6,
	},
	combolg: {
		height: 40,
		paddingLeft: 24,
		paddingRight: 24,
	},
	comboicon: {
		width: 24,
		height: 24,
		padding: 0,
	},
});
